/**
 * MietwagenRueckgabe — Rückgabeprotokoll-Dialog für Mietfahrzeuge (Issue #426).
 *
 * User-Facing-Komponente zum Abschluss einer Mietwagen-Akte:
 *   • Erfassung von KM-Stand, Tankstand, Rückgabedatum/-ort und Schäden
 *   • Foto-Upload für neue Schäden (Storage-Bucket akten-dokumente,
 *     Pfad ${aktenNr}/Rueckgabeprotokoll/fotos/...)
 *   • Kunden-Unterschrift via signature_pad
 *   • PDF-Protokoll via jsPDF (Briefbogen MietGo, analog
 *     MietwagenVertragAbtretung) inkl. eingebetteter Unterschrift
 *   • Persistiert Felder + rueckgabe_protokoll_url + rueckgabe_fotos_url
 *     auf der Akte; Blob-URLs der Foto-Previews werden im Effect-Cleanup
 *     freigegeben (vermeidet Blob-Leak analog #225).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SignaturePad from 'signature_pad';
import { Camera, Check, Loader2, Pen, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import {
  getRueckgabeFotoStoragePath,
  getRueckgabeStoragePath,
} from '@/lib/mietwagen';
import { LCS_BRIEFKOPF } from '@/lib/lcs-briefkopf';

interface AkteLike {
  id?: string | null;
  akten_nummer?: string | null;
  kunde_name?: string | null;
  kennzeichen?: string | null;
  mietfahrzeug_kennzeichen?: string | null;
  mietfahrzeug_marke?: string | null;
  mietfahrzeug_modell?: string | null;
  mietfahrzeug_km_start?: number | null;
  mietfahrzeug_tankstand_start?: string | null;
  mietfahrzeug_km_ende?: number | null;
  mietfahrzeug_tankstand_ende?: string | null;
  uebergabe_datum?: string | null;
  uebergabe_ort?: string | null;
  rueckgabe_datum?: string | null;
  rueckgabe_ort?: string | null;
  rueckgabe_schaeden?: string | null;
  schadennummer?: string | null;
  versicherung?: string | null;
  mietbeginn?: string | null;
}

interface MietwagenRueckgabeProps {
  akte: AkteLike;
  onClose?: () => void;
  onComplete?: () => void;
}

const TANKSTAND_OPTIONS = [
  { value: 'voll', label: 'Voll' },
  { value: '3/4', label: '3/4' },
  { value: '1/2', label: '1/2' },
  { value: '1/4', label: '1/4' },
  { value: 'leer', label: 'Leer' },
] as const;

function formatDatum(d?: string | null): string {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return d;
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function safeFilename(value: string): string {
  return value.replace(/[^a-z0-9._-]/gi, '_');
}

function todayIso(): string {
  return new Date().toISOString().split('T')[0];
}

export default function MietwagenRueckgabe({
  akte,
  onClose,
  onComplete,
}: MietwagenRueckgabeProps) {
  const [kmEnde, setKmEnde] = useState<string>(
    akte.mietfahrzeug_km_ende != null ? String(akte.mietfahrzeug_km_ende) : '',
  );
  const [tankstandEnde, setTankstandEnde] = useState<string>(
    akte.mietfahrzeug_tankstand_ende || 'voll',
  );
  const [rueckgabeDatum, setRueckgabeDatum] = useState<string>(
    akte.rueckgabe_datum || todayIso(),
  );
  const [rueckgabeOrt, setRueckgabeOrt] = useState<string>(
    akte.rueckgabe_ort || 'Altena, Rahmedestr. 3',
  );
  const [schaeden, setSchaeden] = useState<string>(akte.rueckgabe_schaeden || '');
  const [fotos, setFotos] = useState<File[]>([]);
  const [hasUnterschrift, setHasUnterschrift] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const sigCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const sigPadRef = useRef<SignaturePad | null>(null);

  const previews = useMemo(
    () => fotos.map((f) => ({ name: f.name, url: URL.createObjectURL(f) })),
    [fotos],
  );

  useEffect(() => {
    return () => {
      previews.forEach((p) => URL.revokeObjectURL(p.url));
    };
  }, [previews]);

  useEffect(() => {
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext('2d')?.scale(ratio, ratio);
    const pad = new SignaturePad(canvas, {
      backgroundColor: 'rgb(255,255,255)',
      penColor: 'rgb(20,20,20)',
    });
    pad.addEventListener('endStroke', () => {
      setHasUnterschrift(!pad.isEmpty());
    });
    sigPadRef.current = pad;
    return () => {
      pad.off();
      sigPadRef.current = null;
    };
  }, []);

  const clearUnterschrift = useCallback(() => {
    sigPadRef.current?.clear();
    setHasUnterschrift(false);
  }, []);

  const handleFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    setFotos((prev) => [...prev, ...Array.from(list)]);
  };

  const removeFoto = (index: number) => {
    setFotos((prev) => prev.filter((_, i) => i !== index));
  };

  const fahrzeugLabel = useMemo(() => {
    const kennzeichen = akte.mietfahrzeug_kennzeichen || akte.kennzeichen || '—';
    const typ = [akte.mietfahrzeug_marke, akte.mietfahrzeug_modell]
      .filter(Boolean)
      .join(' ');
    return typ ? `${kennzeichen} (${typ})` : kennzeichen;
  }, [akte]);

  const handleSpeichern = async () => {
    if (!akte.id) {
      toast.error('Akte hat keine ID — Speichern nicht möglich');
      return;
    }
    if (!kmEnde.trim() || Number.isNaN(Number(kmEnde))) {
      toast.error('Bitte KM-Stand bei Rückgabe angeben');
      return;
    }
    if (!rueckgabeDatum) {
      toast.error('Bitte Rückgabedatum angeben');
      return;
    }
    const pad = sigPadRef.current;
    if (!pad || pad.isEmpty()) {
      toast.error('Bitte Unterschrift des Kunden erfassen');
      return;
    }

    setIsSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const sigDataUrl = pad.toDataURL('image/png');

      // ── Fotos hochladen ──
      const fotoUrls: string[] = [];
      for (const file of fotos) {
        const filename = `${Date.now()}_${safeFilename(file.name)}`;
        const fotoPath = getRueckgabeFotoStoragePath(akte, filename);
        const { error: fotoErr } = await supabase.storage
          .from('akten-dokumente')
          .upload(fotoPath, file, { upsert: false });
        if (fotoErr) {
          console.error('Foto-Upload fehlgeschlagen:', fotoErr);
          toast.error(`Foto ${file.name} konnte nicht hochgeladen werden`);
          continue;
        }
        const { data: urlData } = supabase.storage
          .from('akten-dokumente')
          .getPublicUrl(fotoPath);
        fotoUrls.push(urlData.publicUrl);
      }

      // ── PDF erzeugen (Briefbogen MietGo) ──
      const { default: jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pw = doc.internal.pageSize.getWidth();
      const m = 15;
      let y = 15;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(232, 93, 4);
      doc.text('MietGo — eine Marke der LCS GmbH', m, y);
      y += 4;
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100);
      doc.text(
        `Vertretungsberechtigt: ${LCS_BRIEFKOPF.vertretungsberechtigt} | Rahmedestrasse 65, 58509 Luedenscheid`,
        m,
        y,
      );
      y += 3;
      doc.text('info@mietgo.de | mietgo.de', m, y);
      y += 2;
      doc.setDrawColor(232, 93, 4);
      doc.setLineWidth(0.8);
      doc.line(m, y, pw - m, y);
      y += 8;

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30);
      doc.text('Rueckgabeprotokoll', m, y);
      y += 8;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`Akte: ${akte.akten_nummer || akte.id}`, m, y);
      y += 4;
      doc.text(`Kunde: ${akte.kunde_name || '—'}`, m, y);
      y += 4;
      doc.text(`Mietfahrzeug: ${fahrzeugLabel}`, m, y);
      y += 6;

      doc.setFont('helvetica', 'bold');
      doc.text('Uebergabe', m, y);
      y += 4;
      doc.setFont('helvetica', 'normal');
      doc.text(
        `Datum: ${formatDatum(akte.uebergabe_datum || akte.mietbeginn)} | Ort: ${akte.uebergabe_ort || '—'}`,
        m,
        y,
      );
      y += 4;
      doc.text(
        `KM-Stand: ${akte.mietfahrzeug_km_start ?? '—'} | Tankstand: ${akte.mietfahrzeug_tankstand_start || '—'}`,
        m,
        y,
      );
      y += 6;

      doc.setFont('helvetica', 'bold');
      doc.text('Rueckgabe', m, y);
      y += 4;
      doc.setFont('helvetica', 'normal');
      doc.text(
        `Datum: ${formatDatum(rueckgabeDatum)} | Ort: ${rueckgabeOrt || '—'}`,
        m,
        y,
      );
      y += 4;
      doc.text(`KM-Stand: ${kmEnde} | Tankstand: ${tankstandEnde}`, m, y);
      y += 6;

      doc.setFont('helvetica', 'bold');
      doc.text('Schaeden bei Rueckgabe', m, y);
      y += 4;
      doc.setFont('helvetica', 'normal');
      const schaedenText = schaeden.trim() || 'Keine Schaeden festgestellt.';
      const schaedenLines = doc.splitTextToSize(schaedenText, pw - 2 * m);
      doc.text(schaedenLines, m, y);
      y += schaedenLines.length * 4 + 4;

      if (fotoUrls.length > 0) {
        doc.setFont('helvetica', 'bold');
        doc.text(`Fotos: ${fotoUrls.length} Bild(er) angehaengt`, m, y);
        y += 6;
      }

      // Unterschrift
      y += 6;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.text('Unterschrift Kunde', m, y);
      y += 2;
      doc.setDrawColor(200);
      doc.setLineWidth(0.3);
      doc.line(m, y, m + 70, y);
      doc.addImage(sigDataUrl, 'PNG', m, y - 15, 60, 15);
      y += 2;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.text(`${akte.kunde_name || ''}, ${formatDatum(rueckgabeDatum)}`, m, y);

      // ── PDF hochladen ──
      const pdfBlob = doc.output('blob');
      const kundeSlug = safeFilename(akte.kunde_name || 'kunde');
      const pdfFilename = `Rueckgabe_${kundeSlug}_${rueckgabeDatum}.pdf`;
      const pdfPath = getRueckgabeStoragePath(akte, pdfFilename);

      const { error: pdfErr } = await supabase.storage
        .from('akten-dokumente')
        .upload(pdfPath, pdfBlob, {
          contentType: 'application/pdf',
          upsert: true,
        });
      if (pdfErr) {
        console.error('PDF-Upload fehlgeschlagen:', pdfErr);
        toast.error('Rückgabeprotokoll konnte nicht gespeichert werden');
        return;
      }

      const { data: pdfUrlData } = supabase.storage
        .from('akten-dokumente')
        .getPublicUrl(pdfPath);

      const updates: Record<string, unknown> = {
        rueckgabe_datum: rueckgabeDatum,
        rueckgabe_ort: rueckgabeOrt || null,
        rueckgabe_schaeden: schaeden.trim() || null,
        mietfahrzeug_km_ende: Number(kmEnde),
        mietfahrzeug_tankstand_ende: tankstandEnde,
        rueckgabe_protokoll_url: pdfUrlData.publicUrl,
      };
      if (fotoUrls.length > 0) {
        updates.rueckgabe_fotos_url = fotoUrls.join(',');
      }

      const { error: updErr } = await supabase
        .from('akten')
        .update(updates)
        .eq('id', akte.id);
      if (updErr) {
        console.error('Akte-Update fehlgeschlagen:', updErr);
        toast.error('Akte konnte nicht aktualisiert werden');
        return;
      }

      toast.success('Rückgabe erfasst', { description: pdfFilename });

      // Status-Übergang: in_miete → zurueckgegeben (Issue #474)
      if (akte.id) {
        const wfRes = await fetch('/api/mietwagen-workflow', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
          body: JSON.stringify({
            action: 'setzeStatus',
            mietwagen_akte_id: akte.id,
            neuer_status: 'zurueckgegeben',
          }),
        });
        if (!wfRes.ok) {
          console.warn('[mietwagen-workflow] Status-Übergang fehlgeschlagen — Akte bleibt im alten Status');
        }
      }

      // PDF-Download für den Kunden
      const downloadUrl = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = pdfFilename;
      a.click();
      URL.revokeObjectURL(downloadUrl);

      onComplete?.();
      onClose?.();
    } catch (err) {
      console.error('Rueckgabe speichern fehlgeschlagen:', err);
      toast.error('Unerwarteter Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b bg-gradient-to-r from-orange-50 to-white">
        <div>
          <h2 className="text-base font-semibold">Mietwagen-Rückgabe erfassen</h2>
          <p className="text-xs text-gray-500">{fahrzeugLabel}</p>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            aria-label="Schließen"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="p-5 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-gray-700">
              Rückgabedatum
            </span>
            <input
              type="date"
              data-testid="rueckgabe-datum"
              value={rueckgabeDatum}
              onChange={(e) => setRueckgabeDatum(e.target.value)}
              className="mt-1 w-full h-10 px-3 rounded-lg border border-gray-200 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-700">Rückgabeort</span>
            <input
              type="text"
              data-testid="rueckgabe-ort"
              value={rueckgabeOrt}
              onChange={(e) => setRueckgabeOrt(e.target.value)}
              placeholder="z.B. Altena, Rahmedestr. 3"
              className="mt-1 w-full h-10 px-3 rounded-lg border border-gray-200 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-700">
              KM-Stand bei Rückgabe
            </span>
            <input
              type="number"
              inputMode="numeric"
              data-testid="rueckgabe-km-ende"
              value={kmEnde}
              onChange={(e) => setKmEnde(e.target.value)}
              placeholder="z.B. 12345"
              className="mt-1 w-full h-10 px-3 rounded-lg border border-gray-200 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-700">
              Tankstand bei Rückgabe
            </span>
            <select
              data-testid="rueckgabe-tankstand-ende"
              value={tankstandEnde}
              onChange={(e) => setTankstandEnde(e.target.value)}
              className="mt-1 w-full h-10 px-3 rounded-lg border border-gray-200 text-sm bg-white"
            >
              {TANKSTAND_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="block">
          <span className="text-xs font-medium text-gray-700">
            Schäden bei Rückgabe
          </span>
          <textarea
            data-testid="rueckgabe-schaeden"
            value={schaeden}
            onChange={(e) => setSchaeden(e.target.value)}
            placeholder="Beschreibung eventueller neuer Schäden am Mietfahrzeug…"
            rows={3}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
          />
        </label>

        <section className="space-y-2">
          <span className="text-xs font-medium text-gray-700">Fotos (Schäden)</span>
          <label className="flex items-center justify-center gap-2 h-12 rounded-xl border-2 border-dashed border-gray-300 bg-white cursor-pointer hover:bg-gray-50">
            <Camera className="w-5 h-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">
              {fotos.length === 0
                ? 'Fotos hinzufügen'
                : `Weitere Fotos (${fotos.length} ausgewählt)`}
            </span>
            <input
              data-testid="rueckgabe-foto-input"
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
          </label>
          {previews.length > 0 && (
            <ul className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {previews.map((p, i) => (
                <li
                  key={p.url}
                  className="relative aspect-square rounded-lg overflow-hidden border bg-gray-100"
                >
                  <img
                    src={p.url}
                    alt={`Foto ${i + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    aria-label={`Foto ${i + 1} entfernen`}
                    onClick={() => removeFoto(i)}
                    className="absolute top-1 right-1 size-6 rounded-full bg-black/60 text-white flex items-center justify-center"
                  >
                    <X className="size-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-700">
              Unterschrift Kunde
            </span>
            <button
              type="button"
              onClick={clearUnterschrift}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
            >
              <Trash2 className="w-3 h-3" />
              Zurücksetzen
            </button>
          </div>
          <div className="rounded-xl border border-gray-300 bg-white relative">
            <canvas
              ref={sigCanvasRef}
              data-testid="rueckgabe-unterschrift"
              className="w-full h-40 rounded-xl touch-none"
            />
            {!hasUnterschrift && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-gray-300 text-sm">
                <Pen className="w-4 h-4 mr-1" /> Hier unterschreiben
              </div>
            )}
          </div>
        </section>

        <div className="flex justify-end gap-2 pt-3 border-t">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              Abbrechen
            </button>
          )}
          <button
            type="button"
            data-testid="rueckgabe-speichern"
            onClick={handleSpeichern}
            disabled={isSaving}
            className="flex items-center gap-2 px-5 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Speichere…
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Rückgabe abschließen & PDF erzeugen
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
